import { useEffect, useState } from 'react';
import type { Membership, Tenant } from '../../api/types.js';

const ROLE_LABELS: Record<Membership['role'], string> = {
  viewer: 'Lecture (viewer)',
  ap_clerk: 'Comptable AP (ap_clerk)',
  ap_approver: 'Validateur AP (ap_approver)',
  ap_admin: 'Admin AP (ap_admin)',
  group_admin: 'Admin groupe (group_admin)',
};

const ROLES: Membership['role'][] = [
  'viewer',
  'ap_clerk',
  'ap_approver',
  'ap_admin',
  'group_admin',
];

interface Props {
  tenants: Tenant[];
  defaultTenantId: string;
  busy: boolean;
  errorMessage: string | null;
  onSubmit: (payload: {
    tenantId: string;
    email: string;
    role: Membership['role'];
    expiresAt: string | null;
  }) => void;
  onClose: () => void;
}

export function InviteMemberModal({
  tenants,
  defaultTenantId,
  busy,
  errorMessage,
  onSubmit,
  onClose,
}: Props) {
  const [email, setEmail] = useState('');
  const [tenantId, setTenantId] = useState(defaultTenantId);
  const [role, setRole] = useState<Membership['role']>('ap_clerk');
  const [expiresAt, setExpiresAt] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const emailValid = /\S+@\S+\.\S+/.test(email);

  function handleSubmit() {
    if (!emailValid || !tenantId) return;
    onSubmit({
      tenantId,
      email: email.trim().toLowerCase(),
      role,
      expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59.000Z`).toISOString() : null,
    });
  }

  return (
    <div
      className="modal__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="invite-modal"
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="invite-title">
        <header className="modal__head">
          <h2 id="invite-title" className="display modal__title">
            Inviter un membre
          </h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </header>

        <div className="invoices__advanced-filters" style={{ flexDirection: 'column', gap: 12 }}>
          <label className="invoices__advanced-field" style={{ width: '100%' }}>
            <span className="eyebrow">Email</span>
            <input
              type="email"
              autoFocus
              placeholder="prenom.nom@altyor.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="invite-email"
            />
          </label>

          <label className="invoices__advanced-field" style={{ width: '100%' }}>
            <span className="eyebrow">Société</span>
            <select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              data-testid="invite-tenant"
            >
              {tenants.map((t) => (
                <option key={t.tenantId} value={t.tenantId}>
                  {t.legalName}
                </option>
              ))}
            </select>
          </label>

          <label className="invoices__advanced-field" style={{ width: '100%' }}>
            <span className="eyebrow">Rôle</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Membership['role'])}
              data-testid="invite-role"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </label>

          <label className="invoices__advanced-field" style={{ width: '100%' }}>
            <span className="eyebrow">Expire le (optionnel)</span>
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              data-testid="invite-expires"
            />
          </label>
        </div>

        {errorMessage ? (
          <div
            className="invoices__error"
            role="alert"
            data-testid="invite-error"
            style={{ marginTop: 12 }}
          >
            {errorMessage}
          </div>
        ) : null}

        <footer className="modal__foot">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Annuler
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !emailValid || !tenantId}
            onClick={handleSubmit}
            data-testid="invite-submit"
          >
            {busy ? 'Envoi…' : 'Inviter'}
          </button>
        </footer>
      </div>
    </div>
  );
}
